import React, { useMemo } from 'react';
import { Sheet, useTheme } from 'tamagui';
import { CobuView } from '@cobu/ui';

import { GroupComment } from '@cobu/types';
import { CobuStack, CobuText } from '@cobu/ui';
import { Strings } from '../../../constants';



interface CommentActionSheetProps {
    comment: GroupComment;
    isCurrentUser: boolean;
    canReport?: boolean;
    onDelete?: () => void;
    onReport?: (userId: string) => void;
    onFlag?: () => void;
    onBlock?: () => void;
    onClose?: () => void;
    onRequestDelete?: () => void;
    onRequestBlock?: () => void;
    onRequestFlag?: () => void;
}

const CommentActionSheet: React.FC<CommentActionSheetProps> = ({ comment, isCurrentUser, canReport = true, onDelete, onReport, onFlag, onBlock, onClose, onRequestDelete, onRequestBlock, onRequestFlag }) => {
    const { commentActions } = Strings.get().discussion;
    const theme = useTheme();

    const hideActionSheet = () => {
        onClose?.();
    };

    const handleDelete = () => {
        onRequestDelete?.();
    };

    const handleBlock = () => {
        onRequestBlock?.();
    };

    const handleFlag = () => {
        onRequestFlag?.();
    };

    const handleReport = () => {
        hideActionSheet();
        onReport?.(comment.user.uuid);
    };

    const handleCancel = () => {
        hideActionSheet();
    };

    // Built dynamically so the sheet sizes to whatever's shown (no filler spacers).
    type Action = { key: string; label: string; onPress: () => void; color?: string };
    const actions: Action[] = isCurrentUser
        ? [{ key: 'delete', label: commentActions.labels.deleteComment, onPress: handleDelete, color: '$red' }]
        : [
              { key: 'block', label: commentActions.labels.blockUser, onPress: handleBlock, color: '$red' },
              ...(canReport
                  ? [{ key: 'report', label: commentActions.labels.reportUser, onPress: handleReport }]
                  : []),
              { key: 'flag', label: commentActions.labels.flagPost, onPress: handleFlag },
          ];

    const estimatedSheetHeight = useMemo(() => {
        const itemHeight = 48;
        const dividerHeight = 8;
        // actions + the Cancel row below the divider
        return itemHeight * (actions.length + 1) + dividerHeight;
    }, [actions.length]);


    return (
        <>
        <Sheet
            open={true}
            onOpenChange={(next: boolean) => {
                if (!next) onClose?.();
            }}
            snapPoints={[estimatedSheetHeight]}
            snapPointsMode="constant"
            dismissOnOverlayPress
            modal
        >
            <Sheet.Overlay animation={undefined as any} enterStyle={{ o: 0 }} exitStyle={{ o: 0 }} bg={theme.blackTransparent50?.val} />
            <Sheet.Frame 
                br="$3" 
                py="$3" 
                gap="$2" 
                bg="$white" 
                borderWidth={1} 
                borderColor="$borderColor"
                style={{
                    boxShadow: `0 -2px 8px rgba(0, 0, 0, 0.1)`, // Using theme black color with 10% opacity
                }}
            >
                <CobuStack>
                    {actions.map((action) => (
                        <ActionItem
                            key={action.key}
                            label={action.label}
                            onPress={action.onPress}
                            color={action.color}
                        />
                    ))}

                    {renderDivider()}

                    <ActionItem label={commentActions.labels.cancel} onPress={handleCancel} />
                </CobuStack>
            </Sheet.Frame>
        </Sheet>
        </>
    );

    function ActionItem({
        label,
        onPress,
        color,
    }: {
        label: string;
        onPress: () => void;
        color?: string;
    }) {
        return (
            <CobuView onPress={onPress}>
                <CobuStack
                    layout="horizontal"
                    ai="center"
                    jc="center"
                    gap="$3"
                    p="$3"
                    br="$2"
                    hoverStyle={{ background: '$backgroundMuted' }}
                >
                    <CobuText variant="body" fos="$3" fow="500" color={color}>
                        {label}
                    </CobuText>
                </CobuStack>
            </CobuView>
        );
    }

    function renderDivider() {
        return <CobuStack h={1} bg="$borderColor" mx="$2" />;
    }
};

CommentActionSheet.displayName = 'CommentActionSheet';
export default CommentActionSheet;